"use client";

import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ComponentProps,
  type ReactNode,
} from "react";
import {
  Button as SharedButton,
  Input as SharedInput,
  Select as SharedSelect,
  Textarea as SharedTextarea,
  Dialog as SharedDialog,
} from "@open-design/components";
import "@fontsource-variable/dm-sans";
import "@fontsource/libre-baskerville/400.css";
import "@fontsource/libre-baskerville/700.css";
import styles from "./Product.module.css";

export {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  VisuallyHidden,
} from "@open-design/components";
export function DesignSurface({ children }: { children: ReactNode }) {
  return <div className={styles.root}>{children}</div>;
}
export const Button = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof SharedButton>
>(function DesignButton({ className, ...props }, ref) {
  return (
    <SharedButton
      ref={ref}
      {...props}
      className={`${styles.button} ${className ?? ""}`}
    />
  );
});
export const Input = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof SharedInput>
>(function DesignInput({ className, ...props }, ref) {
  return (
    <SharedInput
      ref={ref}
      {...props}
      className={`${styles.control} ${className ?? ""}`}
    />
  );
});
export const Select = forwardRef<
  HTMLSelectElement,
  ComponentProps<typeof SharedSelect>
>(function DesignSelect({ className, ...props }, ref) {
  return (
    <SharedSelect
      ref={ref}
      {...props}
      className={`${styles.control} ${className ?? ""}`}
    />
  );
});
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<typeof SharedTextarea>
>(function DesignTextarea({ className, ...props }, ref) {
  return (
    <SharedTextarea
      ref={ref}
      {...props}
      className={`${styles.control} ${className ?? ""}`}
    />
  );
});

export function Dialog({
  className,
  backdropClassName,
  ...props
}: ComponentProps<typeof SharedDialog>) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const previousFocus = useRef(
    typeof document === "undefined"
      ? null
      : (document.activeElement as HTMLElement | null),
  );
  useEffect(() => {
    const panel = document.getElementById(id);
    const overlay = panel?.parentElement;
    if (!panel || !overlay) return;
    const siblings = [...document.body.children].filter(
      (element) => element !== overlay && element instanceof HTMLElement,
    ) as HTMLElement[];
    const priorInert = siblings.map((element) => element.inert);
    siblings.forEach((element) => {
      element.inert = true;
    });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const controls = () =>
      [
        ...panel.querySelectorAll<HTMLElement>(
          'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]',
        ),
      ]
        .sort((a, b) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : 1,
        )
        .filter(
          (element) =>
            !element.closest("[hidden],[inert]") &&
            element.getAttribute("aria-hidden") !== "true",
        );
    if (!panel.contains(document.activeElement)) controls()[0]?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = controls(),
        first = items[0],
        last = items[items.length - 1];
      if (!first) {
        event.preventDefault();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          !panel.contains(document.activeElement))
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !panel.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trap);
    return () => {
      document.removeEventListener("keydown", trap);
      siblings.forEach((element, index) => {
        element.inert = priorInert[index]!;
      });
      document.body.style.overflow = previousOverflow;
      if (previousFocus.current?.isConnected)
        previousFocus.current.focus({ preventScroll: true });
    };
  }, [id]);
  return (
    <SharedDialog
      {...props}
      id={id}
      closeOnEscape={props.closeOnEscape ?? true}
      className={`${styles.dialogPanel} ${className ?? ""}`}
      backdropClassName={`${styles.root} ${styles.dialogBackdrop} ${backdropClassName ?? ""}`}
    />
  );
}
